import { ContainerBreakdown } from '@libs/schemas';

/**
 * Calculate container breakdown from usage data during waste processing
 * This provides real-time breakdown calculation before the order is completed
 *
 * @param containers - Array of container objects with _id and containerTypeInfo
 * @param usageByContainer - Map of container ID to actual days used
 * @param includedDays - Number of days included in the pricing tier
 * @param dailyRateInCents - Cost per day for additional days
 * @param totalDaysPaidAtCheckout - Total prepaid days (for refund calculations)
 * @returns Array of ContainerBreakdown objects
 */
export function calculateContainerBreakdown<
  TContainer extends {
    _id?: string;
    containerTypeInfo?: { size: string; name?: string };
  },
>(
  containers: TContainer[],
  usageByContainer: { [containerId: string]: number },
  includedDays: number,
  dailyRateInCents: number,
  totalDaysPaidAtCheckout: number = 0,
): ContainerBreakdown[] {
  return containers.map((container) => {
    const containerId = container._id || '';
    const usedDays = usageByContainer[containerId] || 0;
    const containerSize = container.containerTypeInfo?.size || 'Unknown';
    const containerName = container.containerTypeInfo?.name || `${containerSize} Dumpster`;

    // Backend logic from apps/trpc/src/admin/order.ts lines 215-218
    // Calculate extra days paid at checkout (beyond included days)
    const extraDaysPaidAtCheckout = Math.max(0, totalDaysPaidAtCheckout - includedDays);

    // Days used beyond included days
    const daysOver = Math.max(0, usedDays - includedDays);

    // Refund only applies to extra days paid (NOT included days)
    const refundDays = Math.max(0, extraDaysPaidAtCheckout - daysOver);
    const refundAmountInCents = refundDays * dailyRateInCents;

    // Additional days beyond all prepaid (included + extra prepaid)
    const additionalDays = Math.max(0, daysOver - extraDaysPaidAtCheckout);
    const additionalCostInCents = additionalDays * dailyRateInCents;

    // Extra days actually used (portion of prepaid extra days)
    const extraDaysPaid = Math.min(daysOver, extraDaysPaidAtCheckout);

    return {
      containerId,
      containerSize,
      containerName,
      usedDays,
      includedDays, // Always show full included days (never reduced)
      extraDaysPaid,
      refundDays,
      refundAmountInCents,
      additionalDays,
      additionalCostInCents,
    };
  });
}
